/**
 * Qué visor abre un documento.
 *
 * ## Por qué existe una función y no un `if` en cada pantalla
 *
 * La decisión «esto es una imagen / esto es un PDF» se toma en cuatro lugares —la miniatura, la
 * vista previa, la ficha y el visor—. Repetida, cada uno la escribe distinto y el día que el
 * contrato cambia hay que acordarse de los cuatro.
 *
 * ## El caso que hace que esto no sea trivial: `mimeType` puede faltar
 *
 * El listado del cartapacio **todavía no publica `mimeType`** (ADR 0022, el campo vive en el asset
 * y no viaja en la lectura), y cuando lo publique va a venir **nullable**: los adjuntos anteriores
 * al modelo de assets no tienen `assetId`, así que no hay de dónde sacarlo.
 * `V2ClientDocumentDownloadSchema` ya lo declara `string | null` en el otro extremo del contrato.
 *
 * Eso convierte `document.mimeType.startsWith('image/')` en una pantalla roja esperando su turno.
 * Acá el ausente es un caso más, no una excepción.
 *
 * ## Lo que NO se hace: adivinar por la extensión del nombre
 *
 * Es la tentación obvia cuando el `mimeType` falta, y está descartada a propósito: el `name` lo
 * escribe una persona en el back-office. Un «Contrato final.pdf» que en realidad es un .docx abriría
 * el visor equivocado, y un archivo sin extensión no se podría clasificar igual. Preferimos el
 * ícono genérico y el visor del sistema —honestos— antes que una adivinanza que falla en silencio.
 */

export const DocumentKind = {
  image: 'image',
  pdf: 'pdf',
  /** Todo lo demás, incluido lo que no se puede determinar. Abre en el visor del sistema. */
  other: 'other'
} as const

export type DocumentKind = (typeof DocumentKind)[keyof typeof DocumentKind]

/**
 * Normaliza un `mimeType` a la forma en la que se puede comparar.
 *
 * Dos detalles del mundo real que un `===` pelado se come:
 * - Los tipos MIME son **case-insensitive** por RFC 2045: `Application/PDF` es válido.
 * - Pueden traer **parámetros**: `application/pdf; charset=binary`. Lo que importa es lo de antes
 *   del `;`.
 */
function normalize(mimeType: string): string {
  return mimeType.split(';')[0].trim().toLowerCase()
}

export function documentKind(mimeType: string | null | undefined): DocumentKind {
  if (!mimeType) return DocumentKind.other

  const normalized = normalize(mimeType)

  if (normalized === 'application/pdf') return DocumentKind.pdf
  if (normalized.startsWith('image/')) return DocumentKind.image

  return DocumentKind.other
}

/** Azúcar para las pantallas, que preguntan por esto más de lo que hacen un `switch`. */
export function isPdf(mimeType: string | null | undefined): boolean {
  return documentKind(mimeType) === DocumentKind.pdf
}

export function isImage(mimeType: string | null | undefined): boolean {
  return documentKind(mimeType) === DocumentKind.image
}
